export type WalkingMotionConfig = {
  id: string;
  label: string;
  motionNo: number;
  stepCount: number;
};

export type WalkingDisplacement = {
  forward: number;
  lateral: number;
  path?: { forward: number; lateral: number }[];
  /** Heading change in degrees (positive turns left); the maze snaps it to the nearest 45°. */
  yaw?: number;
};

/** モーション4の実測移動量（5歩で1マス）を基準にした1歩分の距離。 */
export const WALKING_STEP_DISTANCE_CELLS = 0.2;

export const getWalkingDistanceForStepCount = (stepCount: number) => (
  Math.max(0, stepCount) * WALKING_STEP_DISTANCE_CELLS
);

export const WALKING_MOTION_NUMBERS = new Set([4, 5, 8, 9, 105, 106, 1002, 1006, 1009, 1010]);

export const WALKING_MOTIONS: WalkingMotionConfig[] = [
  { id: '4', motionNo: 4, label: '前進', stepCount: 5 },
  { id: '5', motionNo: 5, label: '後進', stepCount: 5 },
  { id: '8', motionNo: 8, label: '右を向く', stepCount: 0 },
  { id: '9', motionNo: 9, label: '左を向く', stepCount: 0 },
  { id: '1002', motionNo: 1002, label: '左に移動', stepCount: 0 },
  { id: '1006', motionNo: 1006, label: '右に移動', stepCount: 0 },
  { id: '1009', motionNo: 1009, label: '右カーブ歩行', stepCount: 0 },
  { id: '1010', motionNo: 1010, label: '左カーブ歩行', stepCount: 0 },
];

export const DEFAULT_WALKING_MOTION_ID = WALKING_MOTIONS[0].id;
export const DEFAULT_WALKING_STEPS = 5;

/** Motions whose card always carries a count: forward 1000 / backward 1001, side steps 1002 / 1006 and curves 1009 / 1010. */
export const STEPPED_WALKING_MOTION_IDS = new Set(['4', '5', '1002', '1006', '1009', '1010']);

/** Side steps exist only as generated timelines (not in the bundled motion library). */
export const SIDE_STEP_WALKING_MOTION_IDS = new Set(['1002', '1006']);

/** Curve walks also exist only as generated timelines. */
export const CURVE_WALKING_MOTION_IDS = new Set(['1009', '1010']);

/** The legacy fixed "5歩" motions are four counted strides plus an uncounted closing motion. */
const LEGACY_FIVE_STEP_COUNT = 4;
const LEGACY_SINGLE_STEP_MOTIONS: Record<string, { motionId: string; stepCount: number }> = {
  105: { motionId: '4', stepCount: 1 },
  106: { motionId: '5', stepCount: 1 },
};

export const isSteppedWalkingMotion = (id?: string) => STEPPED_WALKING_MOTION_IDS.has(id ?? DEFAULT_WALKING_MOTION_ID);

/** Converts cards saved before step counts were mandatory (4/5 without steps, 105/106) to forward/backward steps. */
export const normalizeWalkingData = <T extends { motionId?: string; stepCount?: number }>(data: T): T => {
  const legacy = data.motionId ? LEGACY_SINGLE_STEP_MOTIONS[data.motionId] : undefined;
  if (legacy) return { ...data, ...legacy };
  if (isSteppedWalkingMotion(data.motionId) && data.stepCount === undefined) {
    const motionId = data.motionId ?? DEFAULT_WALKING_MOTION_ID;
    const stepCount = motionId === '4' || motionId === '5' ? LEGACY_FIVE_STEP_COUNT : DEFAULT_WALKING_STEPS;
    return { ...data, motionId, stepCount };
  }
  return data;
};

export const getWalkingLabel = (id?: string) =>
  WALKING_MOTIONS.find((motion) => motion.id === id)?.label;

export const getWalkingMotionDistanceCells = (motionNo: number) => {
  const motion = WALKING_MOTIONS.find((candidate) => candidate.motionNo === motionNo);
  return motion ? getWalkingDistanceForStepCount(motion.stepCount) : WALKING_STEP_DISTANCE_CELLS;
};